import styled from 'styled-components'
import { SkeletonConsumer } from '@/features/shared/components/SkeletonLoader'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type ProductCardProduct } from '@/features/shop/components/ProductCard/types'
import { screenSizes } from '@shipt/design-system-themes'

type Props = {
  product: ProductCardProduct
  className?: string
  imageSize?: number
  eagerLoad?: boolean
  showLargerProducts?: boolean
  blendMode?: boolean
}

export const ProductCardImage = ({
  product,
  className,
  imageSize = 122,
  eagerLoad = false,
  showLargerProducts = false,
  blendMode = false,
}: Props) => {
  const { name, oos = false, image } = product
  const imageUrl = showLargerProducts ? image.original_size_url : image.url
  const imageQuality = showLargerProducts ? 100 : 75

  return (
    <ImageSkeletonConsumer imageSize={imageSize} showLargerProducts={showLargerProducts}>
      <ImageContainer
        oos={oos}
        className={className}
        data-show-larger-products={showLargerProducts}
        data-blend-mode={blendMode}
      >
        <ProductImage
          src={imageUrl ?? image.url}
          alt={name}
          height={imageSize}
          width={imageSize}
          preload={eagerLoad}
          data-show-larger-products={showLargerProducts}
          quality={imageQuality}
          sizes={`(min-width: 768px) 210px, ${imageSize}px`}
        />
      </ImageContainer>
    </ImageSkeletonConsumer>
  )
}

const ProductImage = styled(Image)<{
  'data-show-larger-products': boolean
}>`
  object-fit: contain;
  border-radius: 0.5rem;

  @media ${screenSizes.tablet} {
    &[data-show-larger-products='true'] {
      height: 210px;
      width: 210px;
    }
  }
`

const ImageSkeletonConsumer = styled(SkeletonConsumer)<{
  imageSize: number
  showLargerProducts: boolean
}>`
  margin-bottom: 1rem;
  height: ${({ imageSize }) => `${imageSize}px`};
  width: 100%;

  @media ${screenSizes.tablet} {
    height: ${({ showLargerProducts, imageSize }) =>
      showLargerProducts ? '210px' : `${imageSize}px`};
  }
`

const ImageContainer = styled.div<{
  oos: boolean
  'data-show-larger-products': boolean
  'data-blend-mode': boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ oos }) => (oos ? '.6' : '1')};
  margin-bottom: 0.5rem;
  width: 9.625rem;
  height: 9.625rem;

  &[data-blend-mode='true'] {
    mix-blend-mode: multiply;
  }

  @media ${screenSizes.tablet} {
    &[data-show-larger-products='true'] {
      width: 100%;
      height: 100%;
    }
  }
`
